import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { notesApi } from '@/lib/notesApi';
import { MessageSquare, Send, Loader2, Trash2, AtSign, Mic, MicOff } from 'lucide-react';
import { DictationHint } from './DictationHint';
import { SPEECH_SUPPORTED, tokenizeDictation, applyOpsToText } from './dictationCommands';

const initialsOf = (name) => (name || '?').trim().slice(0, 1).toUpperCase();

function relTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// Render comment body, highlighting @mentions of known collaborators.
function renderBody(body, names) {
  if (!body) return null;
  if (!names.length) return body;
  // Longest names first so multi-word names match before single tokens.
  const sorted = [...names].sort((a, b) => b.length - a.length);
  const escaped = sorted.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp(`@(${escaped.join('|')})`, 'g');
  const out = [];
  let last = 0; let match; let i = 0;
  while ((match = re.exec(body)) !== null) {
    if (match.index > last) out.push(body.slice(last, match.index));
    out.push(
      <span key={`m-${i++}`} className="rounded bg-[var(--n-accent-bg)] px-1 font-semibold text-[var(--n-accent)]" data-testid="task-comment-mention">{match[0]}</span>,
    );
    last = match.index + match[0].length;
  }
  if (last < body.length) out.push(body.slice(last));
  return out;
}

// ---------------------------------------------------------------------------
// Live task comments with @mention autocomplete. New comments/mentions arrive
// over the shell WebSocket via the `event` prop. `people` are the task
// collaborators (owner + shared users) available to mention.
// ---------------------------------------------------------------------------
export default function TaskComments({ taskId, principal, event, presence = [], people = [], canInvite = false, focusCommentId = null, onInvited }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [mentionMenu, setMentionMenu] = useState(null); // { query, start }
  const [mentionIdx, setMentionIdx] = useState(0);
  const [directory, setDirectory] = useState([]); // Notes users to invite (owner only)
  const [highlightId, setHighlightId] = useState(null); // briefly highlighted comment (jump-to)
  const seenRef = useRef(new Set());
  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const pickedRef = useRef({}); // name(lower) -> { id, invite }
  const focusedDoneRef = useRef(null); // last jump-to comment we already scrolled to
  const focusRef = useRef(focusCommentId); // live mirror so load() can skip auto-scroll-to-end

  // ---- Voice dictation (Web Speech API) — commands + smart spacing ----------
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState('');
  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);

  const insertDictated = useCallback((raw) => {
    const ops = tokenizeDictation(raw);
    if (!ops.length) return;
    setValue((prev) => {
      const ta = taRef.current;
      const start = ta && ta.selectionStart != null ? ta.selectionStart : prev.length;
      const end = ta && ta.selectionEnd != null ? ta.selectionEnd : start;
      const { text, caret } = applyOpsToText(prev, start, end, ops);
      requestAnimationFrame(() => {
        if (taRef.current) {
          taRef.current.focus();
          try { taRef.current.setSelectionRange(caret, caret); } catch { /* noop */ }
        }
      });
      return text;
    });
  }, []);

  const stopDictation = useCallback(() => {
    shouldListenRef.current = false;
    setListening(false);
    setInterim('');
    try { recognitionRef.current && recognitionRef.current.stop(); } catch { /* noop */ }
  }, []);

  const startDictation = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    let rec = recognitionRef.current;
    if (!rec) {
      rec = new SR();
      rec.lang = 'en-US';
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (e) => {
        let finalText = '';
        let interimText = '';
        for (let i = e.resultIndex; i < e.results.length; i += 1) {
          const seg = e.results[i];
          if (seg.isFinal) finalText += seg[0].transcript;
          else interimText += seg[0].transcript;
        }
        if (finalText) insertDictated(finalText);
        setInterim(interimText);
      };
      rec.onerror = (ev) => {
        if (ev.error === 'not-allowed' || ev.error === 'service-not-allowed') {
          shouldListenRef.current = false;
          setListening(false);
          setInterim('');
        }
      };
      rec.onend = () => {
        setInterim('');
        if (shouldListenRef.current) { try { rec.start(); } catch { /* noop */ } }
        else setListening(false);
      };
      recognitionRef.current = rec;
    }
    shouldListenRef.current = true;
    try { rec.start(); setListening(true); } catch { /* already started */ }
  }, [insertDictated]);

  const toggleDictation = useCallback(() => {
    if (listening) stopDictation(); else startDictation();
  }, [listening, startDictation, stopDictation]);

  useEffect(() => () => { shouldListenRef.current = false; try { recognitionRef.current && recognitionRef.current.abort(); } catch { /* noop */ } }, []);
  // Stop listening when switching to another task.
  useEffect(() => { if (listening) stopDictation(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [taskId]);

  const names = useMemo(() => people.map((p) => p.name).filter(Boolean), [people]);

  const scrollToEnd = useCallback(() => {
    requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; });
  }, []);

  const load = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const { data } = await notesApi.get(`/notes/tasks/tasks/${taskId}/comments`);
      seenRef.current = new Set((data || []).map((c) => c.id));
      setComments(data || []);
      if (!focusRef.current) scrollToEnd(); // keep bottom, unless we're about to jump to a mention
    } catch { setComments([]); } finally { setLoading(false); }
  }, [taskId, scrollToEnd]);

  useEffect(() => { pickedRef.current = {}; setValue(''); setMentionMenu(null); load(); }, [load]);

  useEffect(() => {
    if (!event || event.task_id !== taskId) return;
    if (event.kind === 'add' && event.comment && !seenRef.current.has(event.comment.id)) {
      seenRef.current.add(event.comment.id);
      setComments((prev) => [...prev, event.comment]);
      scrollToEnd();
    } else if (event.kind === 'del' && event.comment_id) {
      seenRef.current.delete(event.comment_id);
      setComments((prev) => prev.filter((c) => c.id !== event.comment_id));
    }
  }, [event, taskId, scrollToEnd]);

  // Reset the jump-to tracker when switching tasks.
  useEffect(() => { focusedDoneRef.current = null; }, [taskId]);
  // Keep the live focus mirror in sync (used by load() above).
  useEffect(() => { focusRef.current = focusCommentId; }, [focusCommentId]);

  // Jump-to-comment: when opened from a mention, bring the tagged comment into
  // view (both the outer panel and the inner comments list) and flash a
  // highlight around it — retried a few times to survive the detail re-render.
  useEffect(() => {
    if (!focusCommentId || loading) return undefined;
    if (focusedDoneRef.current === focusCommentId) return undefined;
    if (!comments.some((c) => c.id === focusCommentId)) return undefined;
    const list = scrollRef.current;
    const el = list && list.querySelector(`[data-comment-id="${focusCommentId}"]`);
    if (!el) return undefined;
    focusedDoneRef.current = focusCommentId;
    setHighlightId(focusCommentId);

    const scrollOnce = () => {
      // 1) Bring the comments block into the outer detail-panel viewport.
      try {
        const panel = el.closest('[data-testid="task-detail-panel"]');
        const root = el.closest('[data-testid="task-comments"]') || list;
        if (panel && root) {
          const delta = (root.getBoundingClientRect().top - panel.getBoundingClientRect().top) - (panel.clientHeight / 2) + (root.clientHeight / 2);
          panel.scrollTop = Math.max(0, panel.scrollTop + delta);
        }
      } catch { /* noop */ }
      // 2) Centre the target inside the inner comments list.
      try {
        const off = list.scrollTop + (el.getBoundingClientRect().top - list.getBoundingClientRect().top) - (list.clientHeight / 2) + (el.clientHeight / 2);
        list.scrollTop = Math.max(0, off);
      } catch { /* noop */ }
    };

    // The detail panel keeps growing while the editor/comments finish laying out,
    // so retry until the target is actually inside the panel viewport (or we give
    // up). Fixed one-shot timers ran too early against a shorter layout.
    let attempts = 0;
    let timer = null;
    const settled = () => {
      const panel = el.closest('[data-testid="task-detail-panel"]');
      if (!panel) return true;
      const er = el.getBoundingClientRect();
      const pr = panel.getBoundingClientRect();
      return er.top >= pr.top && er.bottom <= pr.bottom;
    };
    const tick = () => {
      attempts += 1;
      scrollOnce();
      if (!settled() && attempts < 16) { timer = setTimeout(tick, 120); }
    };
    timer = setTimeout(tick, 120);
    const h = setTimeout(() => setHighlightId(null), 3000);
    return () => { if (timer) clearTimeout(timer); clearTimeout(h); };
  }, [focusCommentId, loading, comments]);

  // Detect an in-progress @mention just before the caret.
  const detectMention = (text, caret) => {
    const upto = text.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at === -1) return null;
    const between = upto.slice(at + 1);
    if (/\s/.test(between)) return null; // only a single token after '@'
    return { query: between.toLowerCase(), start: at };
  };

  const onChange = (e) => {
    const text = e.target.value;
    setValue(text);
    const caret = e.target.selectionStart || text.length;
    const m = detectMention(text, caret);
    setMentionMenu(m);
    setMentionIdx(0);
  };

  const filtered = useMemo(() => {
    if (!mentionMenu) return [];
    const q = mentionMenu.query;
    // Only people the task is actually shared with (owner + shared collaborators)
    // are mentionable — anyone else can't read the comment, so mentioning them
    // is pointless. The current user is excluded (you can't mention yourself).
    return people
      .filter((p) => p.id !== principal?.id && (p.name || '').toLowerCase().includes(q))
      .map((p) => ({ id: p.id, name: p.name, is_owner: p.is_owner, invite: false }))
      .slice(0, 8);
  }, [mentionMenu, people, principal]);

  // Owner-only: fetch Notes users matching the in-progress @mention query.
  useEffect(() => {
    if (!canInvite || !mentionMenu) { setDirectory([]); return undefined; }
    let alive = true;
    const q = mentionMenu.query;
    const h = setTimeout(async () => {
      try {
        const { data } = await notesApi.get(`/notes/tasks/directory?q=${encodeURIComponent(q)}`);
        if (alive) setDirectory(data || []);
      } catch { if (alive) setDirectory([]); }
    }, 180);
    return () => { alive = false; clearTimeout(h); };
  }, [canInvite, mentionMenu]);

  const pickMention = (person) => {
    if (!mentionMenu) return;
    const before = value.slice(0, mentionMenu.start);
    const caret = taRef.current ? (taRef.current.selectionStart || value.length) : value.length;
    const after = value.slice(caret);
    const insert = `@${person.name} `;
    const next = before + insert + after;
    pickedRef.current[person.name.toLowerCase()] = { id: person.id, invite: !!person.invite };
    setValue(next);
    setMentionMenu(null);
    requestAnimationFrame(() => {
      const pos = (before + insert).length;
      if (taRef.current) { taRef.current.focus(); taRef.current.setSelectionRange(pos, pos); }
    });
  };

  // Resolve @Name tokens to ids for both existing collaborators and picked
  // (possibly invited) users. Returns { mentions, invites }.
  const computeMentionsAndInvites = (text) => {
    const mentions = new Set();
    const invites = new Set();
    const present = (name) => new RegExp(`@${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`).test(text);
    people.forEach((p) => { if (p.name && present(p.name)) mentions.add(p.id); });
    Object.entries(pickedRef.current).forEach(([nameLower, meta]) => {
      // find the exact-cased name in the text via case-insensitive test
      const re = new RegExp(`@(${nameLower.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})(?=\\s|$)`, 'i');
      if (re.test(text)) {
        mentions.add(meta.id);
        if (meta.invite) invites.add(meta.id);
      }
    });
    return { mentions: Array.from(mentions), invites: Array.from(invites) };
  };

  const submit = useCallback(async () => {
    const body = value.trim();
    if (!body || busy) return;
    setBusy(true);
    const { mentions, invites } = computeMentionsAndInvites(body);
    try {
      const { data } = await notesApi.post(`/notes/tasks/tasks/${taskId}/comments`, { body, mentions, invite_user_ids: invites });
      if (data && !seenRef.current.has(data.id)) {
        seenRef.current.add(data.id);
        setComments((prev) => [...prev, data]);
      }
      setValue(''); pickedRef.current = {};
      scrollToEnd();
      if (invites.length && typeof onInvited === 'function') onInvited(invites);
    } catch { /* keep text to retry */ } finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, busy, taskId, scrollToEnd, people, onInvited]);

  const remove = useCallback(async (c) => {
    try {
      await notesApi.delete(`/notes/tasks/tasks/${taskId}/comments/${c.id}`);
      seenRef.current.delete(c.id);
      setComments((prev) => prev.filter((x) => x.id !== c.id));
    } catch { /* silent */ }
  }, [taskId]);

  const onKey = (e) => {
    if (mentionMenu && filtered.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionIdx((i) => (i + 1) % filtered.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionIdx((i) => (i - 1 + filtered.length) % filtered.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(filtered[mentionIdx]); return; }
      if (e.key === 'Escape') { setMentionMenu(null); return; }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
  };

  const others = (presence || []).filter((u) => u.id !== principal?.id);

  return (
    <div data-testid="task-comments">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">
          <MessageSquare size={13} className="text-[var(--n-accent)]" /> Comments{comments.length ? ` (${comments.length})` : ''}
        </div>
        {others.length > 0 && (
          <div className="flex items-center gap-1" data-testid="task-comments-presence" title={`${others.map((u) => u.name).join(', ')} viewing`}>
            <span className="flex -space-x-1.5">
              {others.slice(0, 3).map((u) => (
                <span key={u.id} className="grid h-5 w-5 place-items-center rounded-full border border-[var(--n-surface)] bg-[var(--n-accent)] text-[9px] font-bold text-white">{initialsOf(u.name)}</span>
              ))}
            </span>
            <span className="text-[10.5px] text-[var(--n-muted)]">viewing</span>
          </div>
        )}
      </div>

      <div ref={scrollRef} className="max-h-64 overflow-y-auto n-scroll pr-1" data-testid="task-comments-list">
        {loading ? (
          <div className="space-y-2 py-1">{[0, 1].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-[var(--n-canvas-2)]" />)}</div>
        ) : comments.length === 0 ? (
          <p className="rounded-xl border border-dashed border-[var(--n-border)] px-3 py-4 text-center text-[12px] text-[var(--n-muted-2)]" data-testid="task-comments-empty">No comments yet. Start the conversation — type @ to mention someone.</p>
        ) : (
          <div className="space-y-2.5">
            {comments.map((c) => {
              const mine = c.author_id === principal?.id;
              return (
                <div key={c.id} data-comment-id={c.id} className="group flex items-start gap-2.5" data-testid="task-comment-item">
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-[var(--n-accent-bg)] text-[10px] font-bold text-[var(--n-accent)]">{initialsOf(c.author_name)}</span>
                  <div className={`min-w-0 flex-1 rounded-xl border px-3 py-2 transition-colors ${highlightId === c.id ? 'border-[var(--n-accent)] bg-[var(--n-accent-bg)] ring-1 ring-[var(--n-accent)]' : 'border-[var(--n-line)] bg-[var(--n-canvas-2)]'}`} data-highlighted={highlightId === c.id ? 'true' : undefined}>
                    <div className="flex items-center gap-2">
                      <span className="truncate text-[12.5px] font-semibold text-[var(--n-ink)]">{mine ? 'You' : c.author_name}</span>
                      <span className="text-[10.5px] text-[var(--n-muted-2)]">{relTime(c.created_at)}</span>
                      {mine && (
                        <button onClick={() => remove(c)} title="Delete comment" data-testid="task-comment-delete"
                          className="ml-auto text-[var(--n-muted-2)] opacity-0 group-hover:opacity-100 hover:text-[var(--n-danger)] transition-opacity"><Trash2 size={12} /></button>
                      )}
                    </div>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-[var(--n-ink-soft)]">{renderBody(c.body, names)}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {listening && (
        <div className="mt-2 flex items-center gap-1.5 rounded-lg bg-[var(--n-danger,#e5484d)]/10 px-2.5 py-1 text-[11.5px] font-medium text-[var(--n-danger,#e5484d)]" data-testid="task-comment-dictate-status">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-current opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-current" />
          </span>
          <span className="truncate">{interim ? interim : 'Listening… say “new line”, “full stop”, “comma”…'}</span>
        </div>
      )}
      <div className="relative mt-2.5 flex items-end gap-2">
        {mentionMenu && filtered.length > 0 && (
          <div className="absolute bottom-full left-0 z-40 mb-1 w-64 overflow-hidden rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-comment-mention-menu">
            <div className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--n-muted-2)]"><AtSign size={11} /> Mention</div>
            {filtered.map((p, idx) => (
              <button key={p.id} type="button" data-testid={p.invite ? 'task-comment-invite-option' : 'task-comment-mention-option'}
                onMouseDown={(e) => { e.preventDefault(); pickMention(p); }}
                className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left ${idx === mentionIdx ? 'bg-[var(--n-accent-bg)]' : 'hover:bg-[var(--n-canvas-2)]'}`}>
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--n-accent)] text-[9px] font-bold text-white">{initialsOf(p.name)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] text-[var(--n-ink)]">{p.name}{p.is_owner ? ' · owner' : ''}</span>
                  {p.invite && p.email && <span className="block truncate text-[11px] text-[var(--n-muted-2)]">{p.email}</span>}
                </span>
                {p.invite && <span className="shrink-0 rounded-full border border-[var(--n-accent)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--n-accent)]">Invite</span>}
              </button>
            ))}
          </div>
        )}
        {SPEECH_SUPPORTED && (
          <div className="flex shrink-0 items-center gap-0.5">
            <button type="button" onClick={toggleDictation} data-testid="task-comment-dictate-button" aria-pressed={listening}
              title={listening ? 'Stop dictation' : 'Dictate (voice to text)'}
              className={`n-focus inline-flex h-[42px] w-[42px] items-center justify-center rounded-xl border transition-colors ${listening
                ? 'border-[var(--n-danger,#e5484d)] bg-[var(--n-danger,#e5484d)]/10 text-[var(--n-danger,#e5484d)]'
                : 'border-[var(--n-border)] bg-[var(--n-surface)] text-[var(--n-muted-2)] hover:text-[var(--n-ink)] hover:bg-[var(--n-canvas-2)]'}`}>
              {listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
            <DictationHint side="top" align="start" />
          </div>
        )}
        <textarea ref={taRef} data-testid="task-detail-comment-input" value={value} rows={1}
          onChange={onChange} onKeyDown={onKey}
          placeholder="Write a comment…  Type @ to mention  (⌘/Ctrl + Enter)"
          className="n-focus max-h-28 min-h-[42px] flex-1 resize-y rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] px-3 py-2.5 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
        <button onClick={submit} disabled={busy || !value.trim()} data-testid="task-detail-comment-submit-button"
          className="n-focus inline-flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-xl bg-[var(--n-accent)] text-white hover:opacity-90 disabled:opacity-50 transition-opacity">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
